import type { ProviderNotification, ProviderNotificationType } from '@/types/provider-notification';

/**
 * Rota + modal de destino de cada notificação, em um lugar só.
 *
 * Regra do sistema: notificação nunca joga o usuário numa lista genérica quando
 * existe id para abrir o item exato. Quem recebe o deep link é a tela de
 * destino, que lê o parâmetro de foco (`FOCUS_PARAM`) com `useFocusParam` e
 * abre o modal/detalhe correspondente.
 */
export const FOCUS_PARAM = {
  charge: 'focusChargeId',
  appointment: 'focusAppointmentId',
  enrollment: 'focusEnrollmentId',
} as const;

export interface NotificationTarget {
  pathname: string;
  params?: Record<string, string>;
}

const CHARGES = '/(tabs)/(provider)/charges';
const DISPUTES = '/(tabs)/(provider)/disputes';
const SCHEDULE = '/(tabs)/(provider)/schedule';
const APPOINTMENT_DETAIL = '/(tabs)/(provider)/schedule/appointment/[id]';
const RECURRENCES = '/(tabs)/(provider)/schedule/recurrences';
const PROFILE = '/(tabs)/(provider)/profile';
/** Recebimentos (PLANO-RECEBIMENTO-AUTOMATICO.md, seção 6.4, T6.1). */
const RECEBIMENTOS = '/(tabs)/(provider)/payments';

/**
 * Notificações antigas (criadas antes das colunas de id) caem no fallback da
 * própria tela — sem modal, mas na tela certa.
 */
export function resolveProviderNotificationTarget(
  notification: Pick<ProviderNotification, 'type' | 'chargeId' | 'appointmentId' | 'enrollmentId'>,
): NotificationTarget {
  const { type, chargeId, appointmentId, enrollmentId } = notification;

  switch (type) {
    case 'APPOINTMENT_REQUESTED':
    case 'APPOINTMENT_BOOKED':
    case 'APPOINTMENT_CANCELLED_BY_CLIENT':
      return appointmentId
        ? { pathname: APPOINTMENT_DETAIL, params: { id: appointmentId } }
        : { pathname: SCHEDULE };

    case 'RECURRING_REQUESTED':
      return enrollmentId
        ? {
            pathname: RECURRENCES,
            params: { [FOCUS_PARAM.enrollment]: enrollmentId },
          }
        : { pathname: RECURRENCES };

    // O perfil mostra o medidor do plano e o botão de trocar de plano.
    case 'PLAN_USAGE_NEAR_LIMIT':
    case 'PLAN_LIMIT_REACHED':
      return { pathname: PROFILE };

    case 'DISPUTE_CREATED':
      return chargeId
        ? { pathname: DISPUTES, params: { [FOCUS_PARAM.charge]: chargeId } }
        : { pathname: DISPUTES };

    case 'PAYMENT_ACCOUNT_ACTIVE':
    case 'PAYMENT_ACCOUNT_ACTION_REQUIRED':
    case 'PAYMENT_ACCOUNT_RESTRICTED':
    case 'PAYMENT_PAYOUT_FAILED':
      return { pathname: RECEBIMENTOS };

    case 'PAYMENT_REPORTED':
    case 'PAYMENT_RECEIVED':
    default:
      return chargeId
        ? { pathname: CHARGES, params: { [FOCUS_PARAM.charge]: chargeId } }
        : { pathname: CHARGES };
  }
}

/** Rótulo curto da ação, usado em toasts/notificações interativas. */
export const NOTIFICATION_ACTION_LABEL: Record<ProviderNotificationType, string> = {
  PAYMENT_REPORTED: 'Conferir pagamento',
  PAYMENT_RECEIVED: 'Ver cobrança',
  DISPUTE_CREATED: 'Ver contestação',
  APPOINTMENT_REQUESTED: 'Responder solicitação',
  APPOINTMENT_BOOKED: 'Ver agendamento',
  APPOINTMENT_CANCELLED_BY_CLIENT: 'Ver agendamento',
  RECURRING_REQUESTED: 'Aprovar recorrência',
  PLAN_USAGE_NEAR_LIMIT: 'Ver meu plano',
  PLAN_LIMIT_REACHED: 'Ver meu plano',
  PAYMENT_ACCOUNT_ACTIVE: 'Ver recebimentos',
  PAYMENT_ACCOUNT_ACTION_REQUIRED: 'Resolver pendência',
  PAYMENT_ACCOUNT_RESTRICTED: 'Ver recebimentos',
  PAYMENT_PAYOUT_FAILED: 'Ver recebimentos',
};
